import { useState, useMemo, useCallback, useEffect } from 'react'
import { SellModelType } from '../types'
import { permissionCheck } from '../../../../../../../../baseComponents/PermissionChecker/function'
import ITHeaderText from '../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/Components/ITHeaderText'
import InputTableLeftColumnForm from '../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/InputTableLeftColumnForm'
import { InputTableLeftTabsProps } from '../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/types'
import { FormElement, FormRender } from '../../../../../../../../forms/FormRenderer/types'
import { parseAddressToOneLine } from '../../../../../../../../utils/globals/address'
import { twinFetchPostJSON } from '../../../../../../../../utils/globals/data'
import { displayDate } from '../../../../../../../../utils/globals/date'
import { TwinDictionary } from '../../../../../../../../utils/globals/dictionary'
import { returnClassOfSpanTemplate } from '../../../../../../../../utils/globals/tailwind'
import useTwinTranslation from '../../../../../../../../utils/hooks/useTwinTranslation'
import { getActiveCurrenciesDictionary, getUserPermissions } from '../../../../../../../../utils/reducers/getters'
import { SellTagsPayload } from '../../../../../../../../utils/reducers/sells/sellTags'
import { ITHeaderOrderTags } from '../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/Components'
import { OrderHeaderURLs } from '../../../../../../../../baseComponents/TwinTable/InputTableLayout/types'
import '../sellHeader.sass'


type SellStepHeaderMainNoPermissionProps = InputTableLeftTabsProps & {
    orderHeaderURLs: OrderHeaderURLs
    sellTags: SellTagsPayload
}

const SellStepHeaderMainNoPermission: React.FC<SellStepHeaderMainNoPermissionProps> = ({ handleChange, headerData, sellTags, setCompleteData }) => {
    const { t } = useTwinTranslation()
    const { memorizedAddresses, customerAddresses, activeCurrencies } = useSellStepHeaderMainNoPermissionLogic({ CustomerAddress: headerData?.CustomerAddress || null, WorkingCenterAddress: headerData?.WorkingCenterAddress || null, CustomerId: headerData?.CustomerId })
    let extraFields: FormElement<keyof SellModelType>[] = []
    if (headerData.CustomerAddressId) {
        extraFields.push({
            name: 'CustomerAddressId',
            component: 'Custom',
            render: ({ value }) => {
                return <ITHeaderText label={t('fiscalAddress', 'Dirección Fiscal')} value={memorizedAddresses.customerAddress} className={returnClassOfSpanTemplate(5)} />
            },
            extraProps: {
                customerAddress: memorizedAddresses.customerAddress
            }
        })
    } else if (customerAddresses) {
        extraFields.push({
            name: 'CustomerAddressId',
            component: 'Custom',
            render: ({ value }) => {
                return <ITHeaderText label={t('fiscalAddress', 'Dirección Fiscal')} value={customerAddresses?.[headerData?.CustomerId]} className={returnClassOfSpanTemplate(5)} />
            },
            extraProps: {
                customerAddresses
            }
        },)
    }
    if (headerData.WorkingCenterAddressId) {
        extraFields.push({
            name: 'WorkingCenterAddressId',
            component: 'Custom',
            render: ({ value, extraProps }) => {
                return <ITHeaderText label={t('workingCenterAddress', 'Dirección centro de trabajo')} value={memorizedAddresses.workingCenterAddress} className={returnClassOfSpanTemplate(5)} />
            },
            extraProps: {
                workingCenterAddress: memorizedAddresses.workingCenterAddress
            }
        },)
    }
    const sections: FormRender<SellModelType> = [
        {
            cols: 5,
            elements: [
                {
                    name: 'CustomerId',
                    component: 'Custom',
                    render: () => <ITHeaderText label={t('customer', 'Cliente')} value={headerData?.Customer?.fiscal_name || headerData?.Customer?.name} className={returnClassOfSpanTemplate(3)} />
                },
                {
                    name: 'full_assigned_number',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('assignedN', 'Nº asignado')} value={value} className={(headerData?.assigned_number ? '' : 'text-red-BA') + ' ' + returnClassOfSpanTemplate(2)} />,
                },
                {
                    name: 'processed_date',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('date', 'Fecha')} value={displayDate(new Date(value))} className={returnClassOfSpanTemplate(5)} />
                },
                {
                    name: 'WorkingCenterId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('soldBy', 'Vendido por')} value={headerData.WorkingCenter.name || ''} className={returnClassOfSpanTemplate(3)} />
                },
                {
                    name: 'WorkingCenterCounterId',
                    component: 'Custom',
                    render: ({ value }) => {
                        return <ITHeaderText label={t('serie', 'Serie')} value={headerData?.WorkingCenterCounter?.serie} className={returnClassOfSpanTemplate(2)} />
                    }
                },
                ...extraFields,
                {
                    name: 'CurrencyId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('currency', 'Moneda')} value={activeCurrencies?.[value]?.name} className={returnClassOfSpanTemplate(2)} />
                },
                {
                    name: 'OrderTags',
                    component: 'Custom',
                    render: ({ extraProps }) => {
                        return (<ITHeaderOrderTags selectedTags={extraProps.selectedTags} dictTags={sellTags || []} headerData={headerData} onChange={handleChange} className={returnClassOfSpanTemplate(5)} readOnly={true} setCompleteData={setCompleteData}/>)
                    },
                    extraProps: {
                        selectedTags: headerData?.tags,
                    }
                },
            ]
        }
    ]
    return (
        <InputTableLeftColumnForm form={sections} handleChange={handleChange} headerData={headerData} />
    )
}

interface SellStepHeaderMainNoPermissionLogicProps {
    CustomerAddress?: TwinDictionary
    WorkingCenterAddress?: TwinDictionary
    CustomerId: number
}

export const useSellStepHeaderMainNoPermissionLogic = ({ CustomerAddress, WorkingCenterAddress, CustomerId }: SellStepHeaderMainNoPermissionLogicProps) => {
    const activeCurrencies = getActiveCurrenciesDictionary()
    const [customerAddresses, setCustomerAddresses] = useState<TwinDictionary | null>(null)

    const memorizedAddresses = useMemo(() => {
        return {
            workingCenterAddress: WorkingCenterAddress ? parseAddressToOneLine(WorkingCenterAddress) : '',
            customerAddress: CustomerAddress ? parseAddressToOneLine(CustomerAddress) : ''
        }
    }, [WorkingCenterAddress, CustomerAddress])

    const getAllCustomerAddress = useCallback(async () => {
        const items = await twinFetchPostJSON('/api/app/customer/customerAddress/getAllCustomerAddresses', { where: { 'CustomerId': CustomerId, 'fiscal_address': true } })
        if (items && items.length) {
            let parsedJSON: TwinDictionary = {}
            for (const key in items) {
                const item = items[key]
                parsedJSON[item['CustomerId']] = parseAddressToOneLine(item)
            }
            const CustomerAdresses = {
                ...parsedJSON,
            }
            setCustomerAddresses({ ...CustomerAdresses })
        }
    }, [setCustomerAddresses, CustomerId])

    useEffect(() => {
        if (!CustomerAddress && permissionCheck(getUserPermissions('customer.profile.address'), 'read')) {
            getAllCustomerAddress()
        }
    }, [getAllCustomerAddress, CustomerAddress])

    return { memorizedAddresses, customerAddresses, activeCurrencies }
}



export default SellStepHeaderMainNoPermission